import { Fragment, useEffect, useState } from "react";
import { ActiveCompetitions } from "./ActiveCompetitions";
import {
  mockLeagueRanking,
  type LeagueRankingRow,
} from "../data/mockLeagueRanking";
import { kaggleTierLabels } from "../data/kaggleTiers";
import { formatAcademicGrade, getGraduateGradeLabel } from "../data/academicGrades";
import { KaggleTierLegend } from "./KaggleTierLegend";
import { getPaginationWindow, Pagination } from "./Pagination";

type RankingResponse = {
  status?: string;
  source?: string;
  year?: number;
  count?: number;
  rankings?: LeagueRankingRow[];
};

type CompetitionDetail = {
  competition_key: string;
  id: number | null;
  slug: string;
  title: string;
  url: string | null;
  rank: number | null;
  participant_count: number | null;
  season_points: number;
  eligible: boolean;
  date: string | null;
  fetched_at: string;
};

type CompetitionDetailsState = {
  status: "loading" | "ready" | "error";
  competitions: CompetitionDetail[];
  message?: string;
};

type RankingState = "loading" | "ready" | "error";
type RankingMainBoard =
  | "middle"
  | "high"
  | "university"
  | "graduate"
  | "overall";
type GradeFilter =
  | "all"
  | "中1"
  | "中2"
  | "中3"
  | "高1"
  | "高2"
  | "高3"
  | "大1"
  | "大2"
  | "大3"
  | "大4"
  | "院1"
  | "院2"
  | "院3"
  | "院4"
  | "院5";

const rankingMainBoards: Array<{
  value: RankingMainBoard;
  label: string;
}> = [
  { value: "middle", label: "中学生" },
  { value: "high", label: "高校生" },
  { value: "university", label: "大学生" },
  { value: "graduate", label: "大学院生" },
  { value: "overall", label: "総合" },
];

const gradeOptionsByBoard: Partial<
  Record<
    RankingMainBoard,
    Array<{ value: Exclude<GradeFilter, "all">; label: string }>
  >
> = {
  middle: [
    { value: "中1", label: "中1" },
    { value: "中2", label: "中2" },
    { value: "中3", label: "中3" },
  ],
  high: [
    { value: "高1", label: "高1" },
    { value: "高2", label: "高2" },
    { value: "高3", label: "高3" },
  ],
  university: [
    { value: "大1", label: "大学1年（高専4年）" },
    { value: "大2", label: "大学2年（高専5年）" },
    { value: "大3", label: "大学3年" },
    { value: "大4", label: "大学4年" },
  ],
  graduate: [
    { value: "院1", label: getGraduateGradeLabel("院1") },
    { value: "院2", label: getGraduateGradeLabel("院2") },
    { value: "院3", label: getGraduateGradeLabel("院3") },
    { value: "院4", label: getGraduateGradeLabel("院4") },
    { value: "院5", label: getGraduateGradeLabel("院5") },
  ],
};

const seasonYear = 2026;
const seasonType = "fiscal";
const rowsPerPage = 20;

function isLocalDevelopment() {
  return ["", "localhost", "127.0.0.1", "::1"].includes(
    window.location.hostname
  );
}

function formatPoints(points: number) {
  return points.toLocaleString("ja-JP", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function formatDate(value: string | null) {
  if (!value) return "開発用サンプルデータ";

  const date = new Date(value.replace(" ", "T"));
  if (Number.isNaN(date.getTime())) return value;

  return `最終更新 ${date.toLocaleString("ja-JP", {
    year: "numeric",
    month: "numeric",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  })}`;
}

function getMainBoard(row: LeagueRankingRow): RankingMainBoard {
  if (row.participant_type === "university" || row.grade === "大学") {
    return "university";
  }
  if (
    row.participant_type === "graduate" ||
    row.participant_type === "exhibition"
  ) {
    return "graduate";
  }
  if (row.participant_type === "professional") return "overall";
  if (["中1", "中2", "中3"].includes(row.grade)) return "middle";
  if (["高1", "高2", "高3"].includes(row.grade)) return "high";
  return "overall";
}

function compareRankingRows(left: LeagueRankingRow, right: LeagueRankingRow) {
  const scoreComparison = right.season_points - left.season_points;
  if (scoreComparison !== 0) return scoreComparison;

  const competitionComparison =
    right.competition_count - left.competition_count;
  if (competitionComparison !== 0) return competitionComparison;

  const leftBestRank = left.best_rank ?? Number.MAX_SAFE_INTEGER;
  const rightBestRank = right.best_rank ?? Number.MAX_SAFE_INTEGER;
  if (leftBestRank !== rightBestRank) return leftBestRank - rightBestRank;

  return left.kaggle_id.localeCompare(right.kaggle_id);
}

function formatCompetitionDate(value: string | null) {
  if (!value) return "—";
  const date = new Date(value.replace(" ", "T"));
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString("ja-JP", {
    year: "numeric",
    month: "numeric",
    day: "numeric",
  });
}

function RankingTable({
  rows,
  exhibition = false,
}: {
  rows: LeagueRankingRow[];
  exhibition?: boolean;
}) {
  const [expandedKaggleId, setExpandedKaggleId] = useState<string | null>(null);
  const [detailsByKaggleId, setDetailsByKaggleId] = useState<
    Record<string, CompetitionDetailsState>
  >({});

  const toggleDetails = async (kaggleId: string) => {
    if (expandedKaggleId === kaggleId) {
      setExpandedKaggleId(null);
      return;
    }

    setExpandedKaggleId(kaggleId);
    if (detailsByKaggleId[kaggleId]) return;

    setDetailsByKaggleId((current) => ({
      ...current,
      [kaggleId]: { status: "loading", competitions: [] },
    }));

    try {
      const params = new URLSearchParams({
        kaggle_id: kaggleId,
        year: String(seasonYear),
        season_type: seasonType,
      });
      const response = await fetch(
        `/api/league_ranking_details.php?${params.toString()}`
      );
      const result = (await response.json().catch(() => ({}))) as {
        status?: string;
        message?: string;
        competitions?: CompetitionDetail[];
      };

      if (!response.ok || result.status !== "ok") {
        throw new Error(result.message || "コンペ詳細を取得できませんでした。");
      }

      setDetailsByKaggleId((current) => ({
        ...current,
        [kaggleId]: {
          status: "ready",
          competitions: Array.isArray(result.competitions)
            ? result.competitions
            : [],
        },
      }));
    } catch (error) {
      setDetailsByKaggleId((current) => ({
        ...current,
        [kaggleId]: {
          status: "error",
          competitions: [],
          message:
            error instanceof Error
              ? error.message
              : "コンペ詳細を取得できませんでした。",
        },
      }));
    }
  };

  return (
    <div className="league-ranking-table-wrap">
      <table className="league-ranking-table">
        <thead>
          <tr>
            <th scope="col">RANK</th>
            <th scope="col">{exhibition ? "PARTICIPANT" : "KAGGLE ID"}</th>
            <th scope="col">CATEGORY</th>
            <th scope="col">GRADE</th>
            <th scope="col">SEASON POINTS</th>
            <th scope="col">COMPETITIONS</th>
            <th scope="col">BEST RESULT</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => {
            const isUniversity =
              row.participant_type === "university" || row.grade === "大学";
            const isProfessional = row.participant_type === "professional";
            const isGraduate =
              !isUniversity &&
              (row.participant_type === "graduate" ||
                row.participant_type === "exhibition");
            const kaggleProfileUrl = `https://www.kaggle.com/${encodeURIComponent(
              row.kaggle_id
            )}`;
            const kaggleTierLabel = row.kaggle_tier
              ? kaggleTierLabels[row.kaggle_tier]
              : null;

            const isExpanded = expandedKaggleId === row.kaggle_id;
            const details = detailsByKaggleId[row.kaggle_id];
            const visibleCompetitions =
              details?.status === "ready"
                ? details.competitions.filter(
                    (competition) => competition.eligible
                  )
                : [];

            const rowKey = `${row.kaggle_id}-${
              row.participant_type ?? "regular"
            }`;

            return (
              <Fragment key={rowKey}>
                <tr
                  key={rowKey}
                  className={`league-ranking-row${
                    isExpanded ? " is-expanded" : ""
                  }`}
                  onClick={() => void toggleDetails(row.kaggle_id)}
                  onKeyDown={(event) => {
                    if (event.key === "Enter" || event.key === " ") {
                      event.preventDefault();
                      void toggleDetails(row.kaggle_id);
                    }
                  }}
                  tabIndex={0}
                  aria-expanded={isExpanded}
                >
                  <td data-label="順位">
                    <span className={`league-ranking-rank rank-${row.rank}`}>
                      {String(row.rank).padStart(2, "0")}
                    </span>
                  </td>
                  <th scope="row" data-label="参加者">
                    <span className="league-ranking-user">
                      <span
                        className="league-ranking-expand-mark"
                        aria-hidden="true"
                      >
                        {isExpanded ? "−" : "+"}
                      </span>
                      <a
                        className="league-ranking-name"
                        data-kaggle-tier={row.kaggle_tier ?? undefined}
                        href={kaggleProfileUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label={`${
                          row.kaggle_id
                        }のKaggleプロフィールを開く${
                          kaggleTierLabel ? `（${kaggleTierLabel}）` : ""
                        }`}
                        title={kaggleTierLabel ?? undefined}
                        onClick={(event) => event.stopPropagation()}
                        onKeyDown={(event) => event.stopPropagation()}
                      >
                        {row.kaggle_id}
                      </a>
                    </span>
                  </th>
                  <td data-label="カテゴリー">
                    <span
                      className={`league-ranking-category${
                        isProfessional
                          ? " is-professional"
                          : isGraduate
                          ? " is-graduate"
                          : isUniversity
                          ? " is-university"
                          : ""
                      }`}
                    >
                      {isProfessional
                        ? "社会人"
                        : isGraduate
                        ? "大学院生"
                        : isUniversity
                        ? "大学生"
                        : "一般参加者"}
                    </span>
                  </td>
                  <td data-label="学年">{formatAcademicGrade(row.grade)}</td>
                  <td className="league-ranking-points" data-label="ポイント">
                    {formatPoints(row.season_points)}
                  </td>
                  <td data-label="参加数">{row.competition_count}</td>
                  <td data-label="最高順位">
                    {row.best_rank ? `${row.best_rank}位` : "—"}
                  </td>
                </tr>
                {isExpanded && (
                  <tr className="league-ranking-details-row">
                    <td className="league-ranking-details-cell" colSpan={7}>
                      <div className="league-ranking-details">
                        <div className="league-ranking-details-heading">
                          <span>COMPETITION DETAILS</span>
                          <strong>{row.kaggle_id}</strong>
                        </div>
                        {!details || details.status === "loading" ? (
                          <p className="league-ranking-details-message">
                            コンペ結果を読み込んでいます…
                          </p>
                        ) : details.status === "error" ? (
                          <p className="league-ranking-details-message is-error">
                            {details.message}
                          </p>
                        ) : visibleCompetitions.length === 0 ? (
                          <p className="league-ranking-details-message">
                            ランキング対象のコンペ結果はまだありません。
                          </p>
                        ) : (
                          <div className="league-ranking-detail-table-wrap">
                            <table className="league-ranking-detail-table">
                              <thead>
                                <tr>
                                  <th scope="col">COMPETITION</th>
                                  <th scope="col">RANK</th>
                                  <th scope="col">PARTICIPANTS</th>
                                  <th scope="col">SEASON POINTS</th>
                                  <th scope="col">DATE</th>
                                </tr>
                              </thead>
                              <tbody>
                                {visibleCompetitions.map((competition) => (
                                  <tr key={competition.competition_key}>
                                    <th scope="row" data-label="コンペ">
                                      {competition.url ? (
                                        <a
                                          href={competition.url}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                        >
                                          {competition.title}
                                        </a>
                                      ) : (
                                        competition.title
                                      )}
                                    </th>
                                    <td data-label="順位">
                                      {competition.rank
                                        ? `${competition.rank}位`
                                        : "未確定"}
                                    </td>
                                    <td data-label="参加チーム数">
                                      {competition.participant_count !== null
                                        ? `${competition.participant_count.toLocaleString(
                                            "ja-JP"
                                          )}チーム`
                                        : "未取得"}
                                    </td>
                                    <td data-label="ポイント">
                                      {formatPoints(competition.season_points)}
                                    </td>
                                    <td data-label="日付">
                                      {formatCompetitionDate(competition.date)}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        )}
                      </div>
                    </td>
                  </tr>
                )}
              </Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

export function LeagueRanking() {
  const [state, setState] = useState<RankingState>("loading");
  const [rows, setRows] = useState<LeagueRankingRow[]>([]);
  const [isMock, setIsMock] = useState(false);
  const [mainBoard, setMainBoard] = useState<RankingMainBoard>("middle");
  const [gradeFilter, setGradeFilter] = useState<GradeFilter>("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    let active = true;

    async function loadRanking() {
      try {
        const response = await fetch(
          `/api/league_ranking.php?year=${seasonYear}&season_type=${seasonType}`
        );
        const result = (await response
          .json()
          .catch(() => ({}))) as RankingResponse;

        if (!response.ok || result.status !== "ok") {
          throw new Error("ランキングを取得できませんでした。");
        }

        if (active) {
          setRows(Array.isArray(result.rankings) ? result.rankings : []);
          setIsMock(false);
          setState("ready");
        }
      } catch {
        if (!active) return;

        if (isLocalDevelopment()) {
          setRows(mockLeagueRanking);
          setIsMock(true);
          setState("ready");
        } else {
          setState("error");
        }
      }
    }

    void loadRanking();
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    setCurrentPage(1);
  }, [gradeFilter, mainBoard, searchQuery]);

  const latestUpdatedAt =
    rows.find((row) => row.updated_at)?.updated_at ?? null;
  const gradeOptions = gradeOptionsByBoard[mainBoard] ?? [];
  const isGradeBoard = gradeOptions.length > 0;
  const selectedGradeLabel =
    gradeOptions.find((option) => option.value === gradeFilter)?.label ??
    gradeFilter;
  const boardName =
    mainBoard === "middle"
      ? "中学生"
      : mainBoard === "high"
      ? "高校生"
      : mainBoard === "university"
      ? "大学生"
      : mainBoard === "graduate"
      ? "大学院生"
      : "総合";
  const boardTitle =
    isGradeBoard && gradeFilter !== "all"
      ? `${selectedGradeLabel}部門`
      : boardName;
  const boardRows =
    mainBoard === "overall"
      ? [...rows]
      : rows.filter((row) => {
          if (getMainBoard(row) !== mainBoard) return false;
          if (!isGradeBoard || gradeFilter === "all") return true;
          return row.grade === gradeFilter;
        });
  const rankedRows = boardRows
    .sort(compareRankingRows)
    .map((row, index) => ({ ...row, rank: index + 1 }));
  const normalizedSearchQuery = searchQuery.trim().toLowerCase();
  const searchedRows = normalizedSearchQuery
    ? rankedRows.filter((row) =>
        row.kaggle_id.toLowerCase().includes(normalizedSearchQuery)
      )
    : rankedRows;
  const pagination = getPaginationWindow(
    searchedRows.length,
    currentPage,
    rowsPerPage
  );
  const visibleRows = searchedRows.slice(
    pagination.startIndex,
    pagination.endIndex
  );
  const boardLabel =
    mainBoard === "overall" ? "OVERALL RANKING" : "DIVISION RANKING";

  return (
    <section
      className="league-simple-section league-ranking-section"
      id="ranking"
      aria-labelledby="ranking-title"
    >
      <div className="league-shell">
        <div className="league-section-top">
          <span className="league-step">01</span>
          <div>
            <p className="league-eyebrow">CURRENT RANKING</p>
            <h2 id="ranking-title">2026 Season Points</h2>
          </div>
        </div>

        <div className="league-ranking-heading">
          <p>
            対象コンペの順位と継続的な挑戦を、シーズンポイントとして集計します。
          </p>
          <span>
            {isMock ? "DEVELOPMENT SAMPLE" : formatDate(latestUpdatedAt)}
          </span>
        </div>

        <KaggleTierLegend />

        <div
          className="league-ranking-tabs"
          role="tablist"
          aria-label="ランキング区分"
        >
          {rankingMainBoards.map((definition) => {
            const count =
              definition.value === "overall"
                ? rows.length
                : rows.filter((row) => getMainBoard(row) === definition.value)
                    .length;

            return (
              <button
                key={definition.value}
                className={mainBoard === definition.value ? "is-active" : ""}
                type="button"
                role="tab"
                aria-selected={mainBoard === definition.value}
                onClick={() => {
                  setMainBoard(definition.value);
                  setGradeFilter("all");
                }}
              >
                {definition.label}
                <span>{count}</span>
              </button>
            );
          })}
        </div>

        {state === "loading" && (
          <p className="league-ranking-message">
            ランキングを読み込んでいます…
          </p>
        )}

        {state === "error" && (
          <p className="league-ranking-message is-error">
            ランキングを取得できませんでした。時間をおいてお試しください。
          </p>
        )}

        {state === "ready" && rows.length === 0 && (
          <p className="league-ranking-message">
            現在、ランキングデータを準備しています。
          </p>
        )}

        {state === "ready" && rows.length > 0 && (
          <section
            className="league-ranking-board"
            aria-labelledby="selected-ranking-title"
          >
            <div className="league-ranking-board-header">
              <div className="league-ranking-group-heading">
                <span>{boardLabel}</span>
                <h3 id="selected-ranking-title">{boardTitle}</h3>
              </div>
              <div className="league-ranking-controls">
                <label className="league-ranking-search">
                  <span>ユーザー検索</span>
                  <input
                    type="search"
                    value={searchQuery}
                    placeholder="Kaggle IDを検索"
                    autoComplete="off"
                    onChange={(event) =>
                      setSearchQuery(event.currentTarget.value)
                    }
                  />
                </label>
                {isGradeBoard && (
                  <div className="league-ranking-grade-filter">
                    <label htmlFor="league-grade-filter">
                      {mainBoard === "university"
                        ? "大学生の学年"
                        : mainBoard === "graduate"
                        ? "大学院生の学年"
                        : "学年で絞り込む"}
                    </label>
                    <select
                      id="league-grade-filter"
                      value={gradeFilter}
                      onChange={(event) =>
                        setGradeFilter(event.currentTarget.value as GradeFilter)
                      }
                    >
                      <option value="all">全員</option>
                      {gradeOptions.map((grade) => (
                        <option key={grade.value} value={grade.value}>
                          {grade.label}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
              </div>
            </div>
            {rankedRows.length === 0 ? (
              <p className="league-ranking-message">
                「{boardTitle}」のランキングデータはまだありません。
              </p>
            ) : searchedRows.length === 0 ? (
              <p className="league-ranking-message">
                「{searchQuery.trim()}」に一致するKaggle
                IDは見つかりませんでした。
              </p>
            ) : (
              <>
                <RankingTable
                  key={`${mainBoard}-${gradeFilter}-${pagination.page}-${normalizedSearchQuery}`}
                  rows={visibleRows}
                  exhibition={
                    mainBoard === "overall" ||
                    mainBoard === "university" ||
                    mainBoard === "graduate"
                  }
                />
                <Pagination
                  currentPage={pagination.page}
                  totalItems={searchedRows.length}
                  pageSize={rowsPerPage}
                  itemUnit="人"
                  ariaLabel="ランキングのページ切り替え"
                  onPageChange={setCurrentPage}
                />
              </>
            )}
          </section>
        )}

        <ActiveCompetitions year={seasonYear} seasonType={seasonType} />

        <p className="league-ranking-note">※ランキングは毎日更新されます。</p>
      </div>
    </section>
  );
}
